import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, TrendUp, Users, ChartBar, Lightning, ArrowRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";

const POS_COLORS = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-4 text-center">
      {Icon && <Icon size={18} className="text-[#ccff00] mx-auto mb-1.5" weight="bold" />}
      <p className="font-mono font-bold text-2xl text-white">{value}</p>
      <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mt-1">{label}</p>
      {sub && <p className="text-[10px] text-[#666] mt-0.5">{sub}</p>}
    </div>
  );
}

function InsightBar({ label, pct, color }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-[#a3a3a3] w-44 shrink-0">{label}</span>
      <div className="flex-1 h-6 bg-[#1a1a1a] rounded-sm overflow-hidden relative">
        <div className="h-full rounded-sm transition-all" style={{ width: `${pct}%`, backgroundColor: color || "#ccff00" }} />
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold text-white">{pct}%</span>
      </div>
    </div>
  );
}

export default function ChampionshipBlueprint() {
  const preloaded = typeof window !== "undefined" && window.__CHAMPIONSHIP_DATA__?.total_champions ? window.__CHAMPIONSHIP_DATA__ : null;
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);

  useEffect(() => {
    (async () => {
      if (!preloaded) setLoading(true);
      try {
        const res = await axios.get(`${API}/championship/blueprint`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, [preloaded]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-10 space-y-4">
        <div className="h-10 w-96 skeleton rounded-sm" />
        <div className="h-64 skeleton rounded-sm" />
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-20 text-center">
        <p className="text-[#a3a3a3]">Championship data not available yet.</p>
      </div>
    </div>
  );

  const { total_champions: total, sf_leagues: sf, oneqb_leagues: oneqb, avg_roster_value: avgVal,
    avg_position_counts: avgPos, top_players_on_rosters: topPlayers, top_starters: topStarters,
    deeper_analysis: deeper, season } = data;

  const topByPos = {};
  for (const p of topPlayers) {
    if (!topByPos[p.position]) topByPos[p.position] = [];
    if (topByPos[p.position].length < 10) topByPos[p.position].push(p);
  }

  const chartData = topPlayers.slice(0, 15).map(p => ({
    name: p.name.split(" ").pop(),
    fullName: p.name,
    pct: p.pct,
    position: p.position,
  }));

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title={`Championship Blueprint ${season}: What Winning Dynasty Rosters Look Like — Dynasty Trade Maker`}
        description={`Analysis of ${total.toLocaleString()} dynasty league champions from the ${season} season. The most common players, position breakdowns, and roster construction patterns on championship-winning teams.`}
        path="/championship-blueprint"
      />
      <PublicNav />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-10" data-testid="blueprint-hero">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">DATA FROM {total.toLocaleString()} DYNASTY LEAGUES</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mb-3">
            CHAMPIONSHIP <span className="text-[#ccff00]">BLUEPRINT</span> {season}
          </h1>
          <p className="text-base text-[#a3a3a3] max-w-2xl mx-auto">What do winning dynasty rosters actually look like? We analyzed {total.toLocaleString()} championship rosters from completed {season} Sleeper dynasty leagues to find out.</p>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="key-stats">
          <StatCard label="Leagues Analyzed" value={total.toLocaleString()} sub={`${sf.toLocaleString()} SF, ${oneqb.toLocaleString()} 1QB`} icon={Trophy} />
          <StatCard label="Avg Roster Value" value={avgVal.toLocaleString()} icon={ChartBar} />
          <StatCard label="Avg WRs Rostered" value={avgPos.WR} sub="Most loaded position" icon={Users} />
          <StatCard label="Top-30 WRs Avg" value={deeper.avg_wr_in_top30} sub="Per champion roster" icon={TrendUp} />
        </div>

        {/* Roster Construction Insights */}
        <div className="border border-[#262626] bg-[#171717] p-5 sm:p-6 mb-8" data-testid="roster-dna">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-4 flex items-center gap-1.5"><Crown size={14} weight="fill" /> CHAMPIONSHIP ROSTER DNA</h2>
          <p className="text-sm text-[#a3a3a3] mb-5">What percentage of championship rosters had elite assets at each position:</p>
          <div className="space-y-3">
            <InsightBar label="Had a top-10 WR" pct={deeper.pct_with_top10_wr} color="#45b7d1" />
            <InsightBar label="Had a premium TE (top 5)" pct={deeper.pct_with_premium_te} color="#f9ca24" />
            <InsightBar label="Had a top-5 RB" pct={deeper.pct_with_top5_rb} color="#4ecdc4" />
            <InsightBar label="Had a top-5 QB" pct={deeper.pct_with_top5_qb} color="#ff6b6b" />
          </div>
          <p className="text-xs text-[#666] mt-4">Based on current FantasyCalc dynasty positional rankings applied to {season} champion rosters.</p>
        </div>

        {/* Avg Position Counts */}
        <div className="border border-[#262626] bg-[#171717] p-5 mb-8">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-4">AVERAGE POSITION ALLOCATION</h2>
          <div className="grid grid-cols-4 gap-3">
            {["QB", "RB", "WR", "TE"].map(pos => (
              <div key={pos} className="text-center p-3 border border-[#262626] bg-[#0a0a0a] rounded-sm">
                <p className="text-[10px] font-bold uppercase" style={{ color: POS_COLORS[pos] }}>{pos}</p>
                <p className="font-mono text-2xl font-bold text-white">{avgPos[pos]}</p>
                <p className="text-[9px] text-[#555]">per roster</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top 15 Chart */}
        <div className="border border-[#262626] bg-[#171717] p-5 mb-8" data-testid="top-players-chart">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-4">MOST COMMON PLAYERS ON CHAMPIONSHIP ROSTERS</h2>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 80 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: "#666" }} domain={[0, 30]} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#a3a3a3" }} width={75} />
              <Tooltip
                contentStyle={{ background: "#171717", border: "1px solid #262626", borderRadius: 2, fontSize: 12 }}
                formatter={(v, n, props) => [`${v}% of champions`, props.payload.fullName]}
              />
              <Bar dataKey="pct" radius={[0, 2, 2, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={POS_COLORS[entry.position] || "#ccff00"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Position Breakdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="position-breakdowns">
          {["QB", "RB", "WR", "TE"].map(pos => (
            <div key={pos} className="border border-[#262626] bg-[#171717] p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: POS_COLORS[pos] }}>TOP {pos}s ON CHAMPION ROSTERS</h3>
              <div className="space-y-1.5">
                {(topByPos[pos] || []).map((p, i) => (
                  <Link key={i} to={`/players/${playerToSlug(p.name)}`} className="flex items-center justify-between py-1 no-underline hover:bg-[#1a1a1a] px-2 rounded-sm transition-colors">
                    <span className="text-xs text-white">{p.name}</span>
                    <span className="text-xs font-mono text-[#ccff00]">{p.pct}%</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center mb-8" data-testid="cta">
          <h3 className="font-heading text-lg text-white uppercase mb-2">HOW DOES YOUR ROSTER COMPARE?</h3>
          <p className="text-xs text-[#888] mb-4">Import your Sleeper league to see how your roster stacks up against championship-winning teams.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold"><Lightning size={12} weight="fill" /> IMPORT YOUR LEAGUE</a>
            <Link to="/rankings" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Value Chart <ArrowRight size={11} /></Link>
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
        </div>
      </div>
    </div>
  );
}
